import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { Reveal, MaskLines } from "@/components/site/Reveal";

/* Lenis owns the scroll position, so jump via window.scrollTo instead of hash anchors */
const jumpTo = (id) => (e) => {
  e.preventDefault();
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 140;
  window.scrollTo({ top, behavior: "smooth" });
};

export const LegalHero = ({ overline, title, subtitle, updated, image }) => (
  <section className="relative pt-40 md:pt-52 pb-20 md:pb-28 overflow-hidden bg-maroon text-cream">
    <div className="absolute inset-0 -z-10 opacity-20">
      <img src={image} alt="" className="w-full h-full object-cover" />
      <div className="absolute inset-0 bg-maroon/70" />
    </div>
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="inline-flex items-center gap-3 text-xs uppercase tracking-[0.3em] text-gold">
        <span className="h-px w-8 bg-gold" />{overline}
      </motion.span>
      <h1 className="mt-6 font-display text-6xl md:text-8xl leading-[0.95] tracking-tight">
        <MaskLines lines={title} start={0.1} />
      </h1>
      {subtitle && (
        <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }} className="mt-8 text-cream/80 text-lg max-w-2xl leading-relaxed">
          {subtitle}
        </motion.p>
      )}
      {updated && (
        <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8, delay: 0.65 }} className="mt-8 text-xs uppercase tracking-[0.25em] text-cream/50">
          Last updated — {updated}
        </motion.p>
      )}
    </div>
  </section>
);

/* Typography helpers so both legal pages read identically */
export const P = ({ children }) => (
  <p className="text-charcoal-muted text-base md:text-lg leading-relaxed">{children}</p>
);

export const UL = ({ items }) => (
  <ul className="space-y-3">
    {items.map((item, i) => (
      <li key={i} className="flex gap-4 text-charcoal-muted text-base md:text-lg leading-relaxed">
        <span className="mt-[0.7em] h-px w-4 bg-gold shrink-0" />
        <span>{item}</span>
      </li>
    ))}
  </ul>
);

export const Note = ({ children }) => (
  <div className="border-l-2 border-gold bg-paper px-6 py-5">
    <p className="text-charcoal-muted text-base leading-relaxed">{children}</p>
  </div>
);

export const LegalBody = ({ sections }) => (
  <section className="py-20 md:py-28 bg-cream">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-20">
      {/* Contents */}
      <aside className="lg:col-span-4">
        <div className="lg:sticky lg:top-36">
          <p className="text-xs uppercase tracking-[0.25em] text-maroon mb-6 flex items-center gap-3">
            <span className="h-px w-6 bg-maroon" />Contents
          </p>
          <ol className="space-y-3 border-l border-charcoal/10 pl-5">
            {sections.map((s, i) => (
              <li key={s.id}>
                <a
                  href={`#${s.id}`}
                  onClick={jumpTo(s.id)}
                  className="group flex gap-3 text-sm text-charcoal-muted hover:text-maroon transition-colors duration-300"
                >
                  <span className="text-gold tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="group-hover:translate-x-1 transition-transform duration-300">{s.title}</span>
                </a>
              </li>
            ))}
          </ol>
        </div>
      </aside>

      {/* Sections */}
      <div className="lg:col-span-8 space-y-14 md:space-y-16">
        {sections.map((s, i) => (
          <Reveal key={s.id}>
            <article id={s.id} className="scroll-mt-40 border-t border-charcoal/10 pt-8">
              <div className="flex items-baseline gap-4">
                <span className="font-display text-3xl text-gold tabular-nums shrink-0">{String(i + 1).padStart(2, "0")}</span>
                <h2 className="font-display text-3xl md:text-4xl tracking-tight text-charcoal leading-tight">{s.title}</h2>
              </div>
              <div className="mt-6 space-y-5">{s.body}</div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const LegalCta = ({ heading, cta = "Get in Touch" }) => (
  <section className="pb-24 md:pb-32 bg-cream">
    <div className="max-w-[1400px] mx-auto px-5 md:px-10">
      <Reveal>
        <div className="bg-maroon text-cream px-8 md:px-16 py-14 md:py-16 flex flex-col md:flex-row items-center justify-between gap-8">
          <h2 className="font-display text-3xl md:text-4xl leading-tight max-w-xl">{heading}</h2>
          <Link to="/contact" className="inline-flex items-center gap-3 bg-gold text-maroon px-10 py-4 text-sm uppercase tracking-[0.18em] hover:bg-gold-hover transition-colors duration-300 shrink-0">
            {cta} <ArrowUpRight size={18} />
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);
